import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Block } from "@blocknote/core";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import { ArrowLeft, Eye, Globe, Save, Upload } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  category: string | null;
  status: "draft" | "published";
  content: Block[];
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type BlogPostPayload = {
  title: string;
  slug: string;
  excerpt?: string;
  category?: string;
  status: "draft" | "published";
  content: Block[];
  featuredImage?: string;
};

async function fetchPostBySlug(slug: string): Promise<BlogPost> {
  const res = await fetch(`/api/admin/blog-posts/by-slug/${slug}`, { credentials: "include" });
  if (!res.ok) {
    throw new Error("Post not found");
  }

  return res.json();
}

async function createPost(body: BlogPostPayload): Promise<BlogPost> {
  const res = await fetch("/api/admin/blog-posts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || "Failed to create post");
  }

  return res.json();
}

async function updatePost(id: string, body: BlogPostPayload): Promise<BlogPost> {
  const res = await fetch(`/api/admin/blog-posts/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || "Failed to update post");
  }

  return res.json();
}

async function uploadImage(file: File): Promise<string> {
  const form = new FormData();
  form.append("image", file);

  const res = await fetch("/api/admin/blog-posts/upload-image", {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!res.ok) {
    throw new Error("Image upload failed");
  }

  const data = await res.json();
  return data.url as string;
}

async function uploadContentImage(file: File): Promise<string> {
  const form = new FormData();
  form.append("image", file);

  const res = await fetch("/api/admin/blog-posts/upload-content-image", {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!res.ok) {
    throw new Error("Image upload failed");
  }

  const data = await res.json();
  return data.url as string;
}

function toSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

const CATEGORIES = ["Climate", "Policy", "Technology", "Research", "Company News", "Other"];
const NO_CATEGORY = "__none__";

export default function BlogEditor() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const canManageBlog = user?.role === "admin" || user?.role === "platform_assistant";

  const [isEditRoute, editParams] = useRoute("/blog/:slug/edit");
  const editSlug = isEditRoute ? editParams?.slug : undefined;
  const isEditMode = !!editSlug;

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [postId, setPostId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<"draft" | "published" | null>(null);

  const editor = useCreateBlockNote({ uploadFile: uploadContentImage });

  const { data: existingPost, isLoading: loadingPost } = useQuery({
    queryKey: ["/api/admin/blog-posts/by-slug", editSlug],
    queryFn: () => fetchPostBySlug(editSlug!),
    enabled: isEditMode && canManageBlog,
  });

  useEffect(() => {
    if (!existingPost) {
      return;
    }

    setPostId(existingPost.id);
    setTitle(existingPost.title);
    setSlug(existingPost.slug);
    setSlugManuallyEdited(true);
    setExcerpt(existingPost.excerpt ?? "");
    setCategory(existingPost.category ?? "");
    setStatus(existingPost.status);
    setFeaturedImage(existingPost.featuredImage ?? null);

    if (Array.isArray(existingPost.content) && existingPost.content.length > 0) {
      editor.replaceBlocks(editor.document, existingPost.content);
    }
  }, [editor, existingPost]);

  useEffect(() => {
    if (!slugManuallyEdited) {
      setSlug(title ? toSlug(title) : "");
    }
  }, [slugManuallyEdited, title]);

  const handleImageUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      setImageUploading(true);
      try {
        const url = await uploadImage(file);
        setFeaturedImage(url);
      } catch {
        toast({ title: "Upload failed", description: "Could not upload image", variant: "destructive" });
      } finally {
        event.target.value = "";
        setImageUploading(false);
      }
    },
    [toast],
  );

  const saveMutation = useMutation({
    mutationFn: async (targetStatus: "draft" | "published") => {
      setPendingAction(targetStatus);

      const payload: BlogPostPayload = {
        title,
        slug,
        excerpt: excerpt || undefined,
        category: category || undefined,
        status: targetStatus,
        content: editor.document as Block[],
        featuredImage: featuredImage || undefined,
      };

      if (isEditMode && postId) {
        return updatePost(postId, payload);
      }

      return createPost(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog-posts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/blog-posts/by-slug"] });
      setPostId(saved.id);
      setStatus(saved.status);
      toast({ title: saved.status === "published" ? "Post published" : "Draft saved" });
      setPendingAction(null);

      if (!isEditMode) {
        navigate(`/blog/${saved.slug}/edit`);
      }
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      setPendingAction(null);
    },
  });

  if (!canManageBlog) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md overflow-hidden rounded-3xl bg-card shadow-sm outline outline-1 outline-offset-[-1px] outline-border">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">Access Denied</h3>
            <p className="mt-1 text-sm text-slate-500">You do not have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && loadingPost) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-8xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => navigate("/blog")}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
            {isEditMode ? "Edit Post" : "New Post"}
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-xl"
            title="Preview saved version"
            disabled={!slug}
            onClick={() => window.open(`/blog/${slug}`, "_blank", "noopener,noreferrer")}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="rounded-2xl"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate("draft")}
          >
            <Save className="mr-1.5 h-4 w-4" />
            {saveMutation.isPending && pendingAction === "draft" ? "Saving..." : "Save Draft"}
          </Button>
          <Button className="rounded-2xl" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate("published")}>
            <Globe className="mr-1.5 h-4 w-4" />
            {saveMutation.isPending && pendingAction === "published" ? "Publishing..." : "Publish"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-5">
      <div className="w-96 flex-none space-y-5 rounded-3xl bg-card p-6 shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border">
        <div className="grid grid-cols-1 gap-5">
          <div className="space-y-1.5">
            <Label className="font-semibold text-slate-700">Title</Label>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Post title"
              className="rounded-2xl bg-slate-50 text-lg font-semibold"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-semibold text-slate-700">Slug</Label>
            <Input
              value={slug}
              onChange={(event) => {
                setSlug(event.target.value);
                setSlugManuallyEdited(true);
              }}
              placeholder="url-friendly-slug"
              className="rounded-2xl bg-slate-50 font-mono text-sm"
            />
            <p className="text-xs text-slate-400">
              Auto-generated from title. Lowercase letters, numbers, and hyphens only.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="font-semibold text-slate-700">Excerpt</Label>
            <Textarea
              value={excerpt}
              onChange={(event) => setExcerpt(event.target.value)}
              placeholder="Short description shown on the listing page"
              className="resize-none rounded-2xl bg-slate-50"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-1 gap-5">
            <div className="space-y-1.5">
              <Label className="font-semibold text-slate-700">Category</Label>
              <Select
                value={category || NO_CATEGORY}
                onValueChange={(value) => setCategory(value === NO_CATEGORY ? "" : value)}
              >
                <SelectTrigger className="rounded-2xl bg-slate-50">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CATEGORY}>No category</SelectItem>
                  {CATEGORIES.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-semibold text-slate-700">Featured Image</Label>
              <div className="flex items-center gap-2">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 transition-colors hover:bg-slate-100">
                    <Upload className="h-4 w-4" />
                    {imageUploading ? "Uploading..." : featuredImage ? "Change image" : "Upload image"}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                  />
                </label>
                {featuredImage ? (
                  <img
                    src={featuredImage}
                    alt="Featured"
                    className="h-10 w-16 rounded-xl border border-slate-200 object-cover"
                  />
                ) : null}
              </div>
            </div>
          </div>

          {isEditMode ? (
            <div className="space-y-1.5">
              <Label className="font-semibold text-slate-700">Status</Label>
              <Input value={status === "published" ? "Published" : "Draft"} readOnly className="rounded-2xl bg-slate-50" />
            </div>
          ) : null}
        </div>
      </div>

      <div className="min-w-[360px] flex-1 rounded-3xl bg-card shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border">
        <div className="border-b border-border/30 px-6 py-4">
          <Label className="text-base font-semibold text-slate-700">Content</Label>
        </div>
        <div className="p-4" style={{ height: "calc(100vh - 220px)", overflowY: "auto" }}>
          <BlockNoteView editor={editor} theme="light" />
        </div>
      </div>
      </div>
    </div>
  );
}
